import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";

const getKey = (searchType, proposalId) => `${searchType}|${proposalId}`;

const useProposalStore = create(
  immer(
    devtools(
      persist(
        (set, get) => ({
          proposals: {},

          // Add a new proposal
          startStreaming: (searchType, proposalId) =>
            set((state) => {
              state.proposals[getKey(searchType, proposalId)] = {
                ...state.proposals[getKey(searchType, proposalId)], // Preserve existing properties
                status: "streaming",
                markdown: "",
                blocks: [],
              };
            }),

          appendProposalMarkdown: (searchType, proposalId, chunk) =>
            set((state) => {
              if (state.proposals[getKey(searchType, proposalId)]) {
                state.proposals[getKey(searchType, proposalId)].markdown +=
                  chunk;
              }
            }),

          updateProposalBlocks: (searchType, proposalId, blocks) =>
            set((state) => {
              if (state.proposals[getKey(searchType, proposalId)]) {
                state.proposals[getKey(searchType, proposalId)].blocks = blocks;
              }
            }),
          updateProposalStatus: (searchType, proposalId, status) =>
            set((state) => {
              if (state.proposals[getKey(searchType, proposalId)]) {
                state.proposals[getKey(searchType, proposalId)].status = status;
              }
            }),

          // Remove a proposal
          removeProposal: (searchType, proposalId) =>
            set((state) => {
              delete state.proposals[getKey(searchType, proposalId)];
            }),

          // Add a new outline
          addOutlineDetails: (
            searchType,
            proposalId,
            outlineDetails,
            files,
            attachments,
            documents
          ) =>
            set((state) => {
              state.proposals[getKey(searchType, proposalId)] = {
                ...state.proposals[getKey(searchType, proposalId)], // Preserve existing properties
                ...outlineDetails,
                files: files, // uploaded files
                attachments: attachments, // contract attachments
                documents: documents, // user documents
              };
            }),

          addSection: (
            searchType,
            proposalId,
            section,
            section_number,
            subsection_number
          ) =>
            set((state) => {
              const outline =
                state.proposals[getKey(searchType, proposalId)]?.outline;
              if (!outline) return;

              if (subsection_number === 0) {
                // Insert as a main section
                outline.main_sections.splice(section_number - 1, 0, section);
              } else {
                // Insert as a subsection
                outline.main_sections[section_number - 1].sub_sections.splice(
                  subsection_number - 1,
                  0,
                  section
                );
              }
            }),

          updateSection: (searchType, proposalId, sectionTitle, updatedData) =>
            set((state) => {
              const outline =
                state.proposals[getKey(searchType, proposalId)]?.outline;
              if (!outline) return;

              outline.main_sections = outline.main_sections.map(
                (mainSection) => {
                  if (mainSection.title === sectionTitle) {
                    return { ...mainSection, ...updatedData };
                  }
                  return {
                    ...mainSection,
                    sub_sections: mainSection.sub_sections?.map((subSection) =>
                      subSection.title === sectionTitle
                        ? { ...subSection, ...updatedData }
                        : subSection
                    ),
                  };
                }
              );
            }),

          deleteSection: (searchType, proposalId, sectionTitle) =>
            set((state) => {
              const outline =
                state.proposals[getKey(searchType, proposalId)]?.outline;
              if (!outline) return;

              outline.main_sections = outline.main_sections
                .map((mainSection) => ({
                  ...mainSection,
                  sub_sections: mainSection.sub_sections?.filter(
                    (subSection) => subSection.title !== sectionTitle
                  ),
                }))
                .filter((mainSection) => mainSection.title !== sectionTitle);
            }),
        }),
        {
          name: "proposal-storage", // Key to store the data in localStorage
          version: 1, // Increment this if you make breaking changes to the store
        }
      ),
      {
        name: "proposal-storage", // redux devtools
        enabled: process.env.NODE_ENV === "development",
      }
    )
  )
);

const useGetOutline = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.outline
  );

const useGetOutlineDocuments = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.documents
  );

const useGetOutlineAttachments = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.attachments
  );

const useGetThreadId = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.thread_id
  );

const useGetAssistantId = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.assistant_id
  );

const useGetOpenaiId = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.openai_ids
  );

const useGetMarkdown = (searchType, proposalId) =>
  useProposalStore(
    (state) => state.proposals[getKey(searchType, proposalId)]?.markdown
  );

export {
  useProposalStore,
  useGetOutline,
  useGetOutlineDocuments,
  useGetOutlineAttachments,
  useGetThreadId,
  useGetAssistantId,
  useGetOpenaiId,
  useGetMarkdown,
};
